import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";

export default function PoliticaDeConfidentialitate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    document.title = "Politica de Confidențialitate — NIS2PASS";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Politica de confidențialitate NIS2PASS - cum colectăm și protejăm datele."
      );
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "description";
      newMeta.content =
        "Politica de confidențialitate NIS2PASS - cum colectăm și protejăm datele.";
      document.head.appendChild(newMeta);
    }
  }, []);

  useEffect(() => {
    const id = "lp-google-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="lp-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --c-navy: #0F172A;
          --c-navy-dark: #1E293B;
          --c-navy-darkest: #0B1220;
          --c-blue: #2563EB;
          --c-blue-dark: #1D4ED8;
          --c-blue-light: #60A5FA;
          --c-orange: #FF8400;
          --c-orange-hover: #FF9520;
          --c-bg-light: #F8FAFC;
          --c-bg-page: #F7FAFF;
          --c-border-light: #E2E8F0;
          --c-border-mid: #CBD5E1;
          --c-text-body: #475569;
          --c-text-muted: #64748B;
          --c-text-dim: #94A3B8;
          --c-text-faint: #CBD5E1;
          --c-white: #FFF;
          --c-black: #111;
        }

        .lp-page { background: var(--c-bg-light); color: var(--c-navy); font-family: 'Sora', system-ui, sans-serif; min-height: 100vh; display: flex; flex-direction: column; }
        .lp-page * { box-sizing: border-box; margin: 0; padding: 0; }
        .lp-page a { text-decoration: none; color: inherit; }

        /* NAV */
        .lp-nav { width: 100%; background: var(--c-navy); padding: 28px 0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .lp-nav-inner { max-width: 1200px; width: 100%; padding: 0 24px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; }
        .lp-nav-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; text-decoration: none; }
        .lp-nav-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; flex-shrink: 0; }
        .lp-nav-brand { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: var(--c-white); }
        .lp-nav-links { display: flex; align-items: center; gap: 24px; }
        .lp-nav-link { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; }
        .lp-nav-link:hover { color: var(--c-white); }
        .lp-nav-link.active { color: var(--c-blue-light); }
        .lp-nav-btns { display: flex; align-items: center; gap: 12px; }
        .lp-nav-ghost { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-faint); background: transparent; border: 1px solid #334155; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 200ms; }
        .lp-nav-ghost:hover { border-color: var(--c-blue-light); color: var(--c-white); background: rgba(96,165,250,0.08); }
        .lp-nav-cta { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-black); background: var(--c-orange); border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 200ms; }
        .lp-nav-cta:hover { background: var(--c-orange-hover); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }

        /* MAIN CONTENT */
        .lp-main { flex: 1; max-width: 1120px; width: 100%; margin: 0 auto; padding: 80px 24px; }

        /* HEADER */
        .lp-label { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-blue); letter-spacing: 1.4px; text-transform: uppercase; margin-bottom: 12px; }
        .lp-title { font-family: 'Sora', sans-serif; font-size: 38px; font-weight: 700; color: var(--c-navy); letter-spacing: -1.2px; line-height: 1.08; margin-bottom: 32px; }

        /* DOCUMENT CARD */
        .lp-card { background: var(--c-white); border: 1px solid var(--c-border-light); border-radius: 8px; padding: 32px 36px; position: relative; }

        .lp-doc-title { font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 700; color: var(--c-navy); margin-bottom: 8px; }
        .lp-doc-date { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 4px; }
        .lp-doc-company { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-muted); margin-bottom: 16px; }
        .lp-doc-divider { border: none; height: 1px; background: var(--c-border-light); margin: 16px 0 24px; }
        .lp-doc-heading { font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 700; color: var(--c-navy); margin: 24px 0 8px; }
        .lp-doc-body { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin-bottom: 8px; }
        .lp-doc-body strong { color: var(--c-navy); font-weight: 700; }
        .lp-doc-list { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--c-text-body); line-height: 1.45; margin: 4px 0 12px 20px; list-style: disc; }
        .lp-doc-list li { margin-bottom: 4px; }
        .lp-doc-list li strong { color: var(--c-navy); font-weight: 700; }

        /* RESPONSIVE */
        @media (max-width: 768px) {
          .lp-main { padding: 48px 24px; }
          .lp-title { font-size: 28px; letter-spacing: -0.8px; margin-bottom: 24px; }
          .lp-card { padding: 24px 20px; }
          .lp-doc-title { font-size: 20px; }
          .lp-nav-links, .lp-nav-btns { display: none; }
        }
      `}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <a href="/" className="lp-nav-logo">
            <span className="lp-nav-logo-mark" />
            <span className="lp-nav-brand">NIS2PASS</span>
          </a>
          <div className="lp-nav-links">
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/")}
            >
              Platforma
            </button>
            <button
              type="button"
              className="lp-nav-link active"
              onClick={() => navigate("/termeni-si-conditii")}
            >
              Legal
            </button>
            <button
              type="button"
              className="lp-nav-link"
              onClick={() => navigate("/contact")}
            >
              Contact
            </button>
          </div>
          <div className="lp-nav-btns">
            {isLoggedIn ? (
              <>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/dashboard")}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/logout")}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lp-nav-ghost"
                  onClick={() => navigate("/auth?mode=signin")}
                >
                  Intra in cont
                </button>
                <button
                  type="button"
                  className="lp-nav-cta"
                  onClick={() => navigate("/auth")}
                >
                  Creeaza cont gratuit
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* MAIN */}
      <main className="lp-main">
        <p className="lp-label">DOCUMENTE LEGALE NIS2PASS</p>
        <h1 className="lp-title">Politica de Confidențialitate</h1>
        <div className="lp-card">
          <h2 className="lp-doc-title">
            Politica de Confidențialitate — Versiunea 3.0
          </h2>
          <p className="lp-doc-date">22 Septembrie 2025</p>
          <p className="lp-doc-company">
            Compliance Made Easy SRL · CUI: 48993456 · J2023019988400
          </p>
          <hr className="lp-doc-divider" />

          <h3 className="lp-doc-heading">1. Introducere</h3>
          <p className="lp-doc-body">
            Confidențialitatea datelor dumneavoastră este o prioritate pentru
            Compliance Made Easy SRL (&quot;Compania&quot;, &quot;noi&quot;).
            Această Politică de Confidențialitate explică ce date cu caracter
            personal colectăm, de ce le colectăm și cum le utilizăm atunci când
            folosiți platforma NIS2PASS (&quot;Platforma&quot;).
          </p>
          <p className="lp-doc-body">
            Compania acționează în calitate de Operator de Date în conformitate
            cu Regulamentul (UE) 2016/679 (GDPR).
          </p>
          <p className="lp-doc-body">
            Pentru orice întrebări sau pentru exercitarea drepturilor
            dumneavoastră, ne puteți contacta la adresa de e-mail:
            contact@nis2pass.ro. Pentru solicitări specifice privind protecția
            datelor, inclusiv ștergerea acestora, vă rugăm să scrieți la:
            dataprotection@nis2pass.ro.
          </p>

          <h3 className="lp-doc-heading">2. Ce Date Colectăm</h3>
          <p className="lp-doc-body">
            Colectăm și prelucrăm următoarele categorii de date:
          </p>
          <p className="lp-doc-body">
            <strong>Date furnizate direct de Utilizator:</strong>
          </p>
          <ul className="lp-doc-list">
            <li>
              Date de identificare și contact: Nume, prenume, adresă de e-mail,
              funcție în cadrul entității pe care o reprezentați.
            </li>
            <li>
              Date de cont: Nume de utilizator, parolă (stocată în format
              criptat).
            </li>
            <li>
              Date de facturare: Datele companiei (denumire, CUI, adresă),
              necesare pentru procesarea plăților și emiterea facturilor.
            </li>
          </ul>
          <p className="lp-doc-body">
            <strong>Date colectate automat:</strong>
          </p>
          <ul className="lp-doc-list">
            <li>
              Date tehnice: Adresa IP, tipul și versiunea browserului, sistemul
              de operare, informații despre dispozitiv.
            </li>
            <li>
              Date de utilizare: Paginile vizitate, funcțiile utilizate, timpul
              petrecut pe Platformă, jurnale de activitate (log-uri).
            </li>
          </ul>
          <p className="lp-doc-body">
            <strong>
              Date prelucrate prin intermediul Serviciului (Date Publice):
            </strong>
          </p>
          <ul className="lp-doc-list">
            <li>
              Atunci când introduceți un CUI, Platforma preia date despre
              entitatea juridică respectivă din surse publice, prin intermediul
              operatorilor noștri.
            </li>
          </ul>

          <h3 className="lp-doc-heading">
            3. Sursele Datelor și Tipurile de Date Publice Prelucrate
          </h3>
          <p className="lp-doc-body">
            Platforma NIS2PASS nu colectează direct date din sursele primare, ci
            utilizează servicii ale unor operatori terți care agregă date cu
            caracter public de la diverse autorități și registre din România,
            inclusiv cele disponibile pe portalul data.gov.ro.
          </p>

          <h3 className="lp-doc-heading">
            4. Scopurile și Temeiurile Legale ale Prelucrării
          </h3>
          <p className="lp-doc-body">
            Prelucrăm datele dumneavoastră în scopuri legitime și bine definite,
            bazându-ne pe temeiurile legale prevăzute de GDPR.
          </p>

          <h3 className="lp-doc-heading">5. Perioada de Retenție a Datelor</h3>
          <p className="lp-doc-body">
            Conform solicitării dumneavoastră, datele contului de utilizator
            sunt păstrate până la ștergerea explicită a contului. Cu toate
            acestea, politica noastră este nuanțată pentru a respecta
            obligațiile legale:
          </p>
          <ul className="lp-doc-list">
            <li>
              <strong>Datele de Cont:</strong> Sunt șterse la cererea explicită
              a Utilizatorului, transmisă la dataprotection@nis2pass.ro.
            </li>
            <li>
              <strong>Obligații Legale:</strong> Anumite date, precum cele din
              facturi, vor fi păstrate pentru perioada minimă impusă de lege
              (ex: 10 ani pentru documente contabile), chiar și după ștergerea
              contului.
            </li>
            <li>
              <strong>Date Agregate:</strong> După ștergerea contului, putem
              păstra date agregate și anonimizate în scopuri statistice și de
              analiză, care nu permit identificarea personală.
            </li>
          </ul>

          <h3 className="lp-doc-heading">6. Dezvăluirea Datelor către Terți</h3>
          <p className="lp-doc-body">
            Nu vindem și nu închiriem datele dumneavoastră personale. Putem
            dezvălui datele către următoarele categorii de destinatari:
          </p>
          <ul className="lp-doc-list">
            <li>
              <strong>Furnizori de servicii:</strong> Companii care ne oferă
              servicii de găzduire (hosting), procesare de plăți, analiză de
              date sau marketing prin e-mail.
            </li>
            <li>
              <strong>Operatori de date publice:</strong> Partenerii care ne
              furnizează accesul la datele publice agregate.
            </li>
            <li>
              <strong>Autorități publice:</strong> La solicitarea legală a
              acestora, în conformitate cu legislația în vigoare.
            </li>
          </ul>

          <h3 className="lp-doc-heading">
            7. Transferuri Internaționale de Date
          </h3>
          <p className="lp-doc-body">
            Datele dumneavoastră sunt stocate și prelucrate, în principal, pe
            servere aflate în Spațiul Economic European (SEE).
          </p>

          <h3 className="lp-doc-heading">8. Drepturile Dumneavoastră (GDPR)</h3>
          <p className="lp-doc-body">
            În calitate de persoană vizată, aveți următoarele drepturi:
          </p>
          <ul className="lp-doc-list">
            <li>Dreptul de acces</li>
            <li>Dreptul la rectificare</li>
            <li>Dreptul la ștergere (&quot;dreptul de a fi uitat&quot;)</li>
            <li>Dreptul la restricționarea prelucrării</li>
            <li>Dreptul la portabilitatea datelor</li>
            <li>Dreptul la opoziție</li>
            <li>Dreptul de a nu fi supus unei decizii individuale automate</li>
            <li>Dreptul de a depune o plângere la ANSPDCP</li>
          </ul>

          <h3 className="lp-doc-heading">9. Securitatea Datelor</h3>
          <p className="lp-doc-body">
            Implementăm măsuri tehnice și organizatorice adecvate pentru a
            proteja datele dumneavoastră împotriva accesului neautorizat,
            modificării, dezvăluirii sau distrugerii.
          </p>

          <h3 className="lp-doc-heading">10. Politica de Cookies</h3>
          <p className="lp-doc-body">
            Platforma noastră utilizează fișiere de tip cookie pentru a asigura
            funcționalitatea, a îmbunătăți experiența de navigare și pentru
            analiză.
          </p>

          <h3 className="lp-doc-heading">11. Modificări ale Politicii</h3>
          <p className="lp-doc-body">
            Ne rezervăm dreptul de a actualiza această Politică de
            Confidențialitate.
          </p>
        </div>
      </main>

      {/* FOOTER */}
      <SharedFooter />
    </div>
  );
}
